define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Util/array2Json',
    '../Util/json2Array',
    '../System/Vec3Obj',
    '../System/ColorObj',
    '../Enum/EnumPlottingStatus',
    '../Enum/EnumPlottingType'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    array2Json,
    json2Array,
    Vec3Obj,
    ColorObj,
    EnumPlottingStatus,
    EnumPlottingType
) {
    'use strict';

    /**
     * 态势标绘体。
     * <br /><br />
     * <div align='center'>
     * <img src='images/Plotting.png' width='900' height='650' /><br />
     * 态势标绘效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/Plotting.html|CooWasm Plotting demo}
     * @alias Plotting
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumPlottingType} [props.type=EnumPlottingType.POLYLINE] 态势标绘类型。
     * @param {EnumPlottingStatus} [props.status=EnumPlottingStatus.CREATE] 态势标绘编辑状态。
     * @param {Array.<Vec3Obj>} [props.controlPoints] 控制点数组。
     * @param {ColorObj|String} [props.controlPointColor=new ColorObj(1.0,1.0,0,1.0)] 控制点颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.controlPointSize=10] 控制点大小。
     * @param {Number} [props.baseHeight] 基准高度。
     * @param {ColorObj} [props.controlLineColor=new ColorObj(1.0,1.0,0,1.0)] 控制线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.controlLineWidth=10] 控制线宽度。
     * @param {ColorObj} [props.lineColor=new ColorObj(1.0,0.0,0,1.0)] 实际绘制线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.lineWidth=10] 实际绘制线宽度。
     * @param {ColorObj} [props.surfaceColor=new ColorObj(0.0,1.0,0,1.0)] 实际绘制面颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * var plot = new CooWasm.Plotting({},(state)=>{console.log("load status:" + state)});
     */
    class Plotting {
        /**
         * @description 态势标绘类型。
         * @type {EnumPlottingType}
         */
        type;
        /**
         * @description 态势标绘编辑状态。
         * @type {EnumPlottingStatus}
         */
        status;
        /**
         * @description 控制点数组。
         * @type {Array.<Vec3Obj>}
         */
        controlPoints;
        /**
         * @description 控制点颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        controlPointColor;
        /**
         * @description 控制点大小。
         * @type {Number}
         */
        controlPointSize;
        /**
         * @description 基准高度。
         * @type {Number}
         * @readonly
         */
        baseHeight;
        /**
         * @description 控制线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        controlLineColor;
        /**
         * @description 控制线宽度。
         * @type {Number}
         */
        controlLineWidth;
        /**
         * @description 实际绘制线颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        lineColor;
        /**
         * @description 实际绘制线宽度。
         * @type {Number}
         */
        lineWidth;
        /**
         * @description 实际绘制面颜色,可以是ColorObj对象，或者16进制颜色，如#FFFFFFFF。
         * @type {ColorObj|String}
         */
        surfaceColor;
        /**
        * @description 最小可见高度。
        * @type {Number}
        */
        minAltitude;
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude;
        /**
         * @description 态势标绘显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true;
        /**
         * @description 态势标绘对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null;
        /**
         * @description 长方体组件uuid。
         * @type {String}
         * @readonly
         */
        componentPlottingUuid = null;
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null;
        /**
         * @description GeoSphere在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Plotting";
        static avatarType = "deSMP.Avatar.EmptyAvatar";
        static componentPlottingType = "deSMP.AvatarComponent.PlottingComponent";
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent";

        constructor(props, callback) {
            if (!defined(props)) {
                props = {};
            }
            if (defined(props.build) && props.build == true) {
                return;
            }
            let bSync = true;
            this.type = defaultValue(props.type, EnumPlottingType.POLYLINE);
            this.status = defaultValue(props.status, EnumPlottingStatus.CREATE);
            if (this.status == EnumPlottingStatus.SHOW) {
                if (!defined(props.controlPoints)) {
                    console.log("Plotting need controlPoints!");
                    return;
                }
            }
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.EmptyAvatar";
            let componentCategoryName = Plotting.componentPlottingType + "#$%" + Plotting.componentAltitudeVisCtrlType + "#$%";
            let avatarProps = "";
            let componentProps = "";
            //create avatars
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible");

            let plottingComponentProps = "";
            plottingComponentProps += this.toStr("type", this.type, "PlottingType");
            plottingComponentProps += this.toStr("status", this.status, "EditMode");
            if (this.status == EnumPlottingStatus.SHOW) {
                plottingComponentProps += "ControlPoints:" + array2Json("TUPLE3D", props.controlPoints) + "|";
            }
            plottingComponentProps += this.toColorStr("controlPointColor", props.controlPointColor, "ControlPointColor");
            plottingComponentProps += this.toStr("controlPointSize", props.controlPointSize, "ControlPointSize");
            plottingComponentProps += this.toStr("baseHeight", props.baseHeight, "BottomHeight");
            plottingComponentProps += this.toColorStr("controlLineColor", props.controlLineColor, "ControlLineColor");
            plottingComponentProps += this.toStr("controlLineWidth", props.controlLineWidth, "ControlLineWidth");
            plottingComponentProps += this.toColorStr("lineColor", props.lineColor, "PlottingLineColor");
            plottingComponentProps += this.toStr("lineWidth", props.lineWidth, "PlottingLineWidth");
            plottingComponentProps += this.toColorStr("surfaceColor", props.surfaceColor, "PlottingSurfaceColor");

            componentProps += plottingComponentProps + "#$%";
            let altitudeVisCtrlComponentProps = "";
            altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude");
            altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude");
            componentProps += altitudeVisCtrlComponentProps + "#$%";
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(componentCategoryName), encode(componentProps), bSync, this.bVisible, true);
            // var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), this.bVisible, true);
            if (idInfo != "") {
                var idArray = idInfo.split('#$%');
                this.avatarUuid = idArray[0];
                this.componentPlottingUuid = idArray[1];
                this.componentAltitudeVisCtrlUuid = idArray[2];
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    if (this.status == EnumPlottingStatus.SHOW) {
                        this.getProperties();
                        if (defined(callback)) {
                            callback(state);
                        }
                        Element.prototype.Map.plottings.push(this);
                    } else {
                        callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                            this.getProperties();
                            if (defined(callback)) {
                                callback(state);
                            }
                            Element.prototype.Map.plottings.push(this);
                        })
                    }
                })
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {EnumPlottingType} [props.type] 态势标绘类型。
         * @param {EnumPlottingStatus} [props.status] 态势标绘编辑状态。
         * @param {Array.<Vec3Obj>} [props.controlPoints] 控制点数组。
         * @param {ColorObj} [props.controlPointColor] 控制点颜色。
         * @param {Number} [props.controlPointSize] 控制点大小。
         * @param {Number} [props.baseHeight] 基准高度。
         * @param {ColorObj} [props.controlLineColor] 控制线颜色。
         * @param {Number} [props.controlLineWidth] 控制线宽度。
         * @param {ColorObj} [props.lineColor] 实际绘制线颜色。
         * @param {Number} [props.lineWidth] 实际绘制线宽度。
         * @param {ColorObj} [props.surfaceColor] 实际绘制面颜色。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback=null] 更新成功后触发的函数回调。
         * @example
         * plot.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback) {
            let avatarProps = "";
            let componentProps = "";
            let plottingComponentProps = "";
            let altitudeVisCtrlComponentProps = "";
            let uuids = "";
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible");

                plottingComponentProps += this.toStr("type", props.type, "PlottingType");
                plottingComponentProps += this.toStr("status", props.status, "EditMode");
                plottingComponentProps += this.toColorStr("controlPointColor", props.controlPointColor, "ControlPointColor");
                plottingComponentProps += this.toStr("controlPointSize", props.controlPointSize, "ControlPointSize");
                plottingComponentProps += this.toStr("baseHeight", props.baseHeight, "BottomHeight");
                plottingComponentProps += this.toColorStr("controlLineColor", props.controlLineColor, "ControlLineColor");
                plottingComponentProps += this.toStr("controlLineWidth", props.controlLineWidth, "ControlLineWidth");
                plottingComponentProps += this.toColorStr("lineColor", props.lineColor, "PlottingLineColor");
                plottingComponentProps += this.toStr("lineWidth", props.lineWidth, "PlottingLineWidth");
                plottingComponentProps += this.toColorStr("surfaceColor", props.surfaceColor, "PlottingSurfaceColor");
                if (defined(props.controlPoints)) {
                    this.controlPoints = props.controlPoints;
                    plottingComponentProps += "ControlPoints:" + array2Json("TUPLE3D", props.controlPoints) + "|";
                }
                //altitudeVisCtrlComponent
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude");
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude");

                uuids += this.componentPlottingUuid + "#$%";
                uuids += this.componentAltitudeVisCtrlUuid + "#$%";
                componentProps += plottingComponentProps + "#$%";
                componentProps += altitudeVisCtrlComponentProps + "#$%";

                if (this.status == EnumPlottingStatus.SHOW) {
                    callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                        this.getProperties();
                        if (defined(callback)) {
                            callback(state);
                        }
                    });
                } else {
                    callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                        this.getProperties();
                        if (defined(callback)) {
                            callback(state);
                        }
                    });
                }
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback));
                // Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentPlottingUuid), encode(avatarProps), encode(componentProps), defined(callback));
            }
        }
        toStr(memName, value, key) {
            let result = "";
            if (defined(value)) {
                this[memName] = value;
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|";
                } else {
                    result = key + ":" + value + "|";
                }
            }
            return result;
        }
        toColorStr(memName, value, key){
            let result = "";
            if (defined(value)) {
                this[memName] = value;
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toHexStr() + "|";
                } else {
                    if (typeof value === 'string') {
                        let color = ColorObj.fromCssColorString(value);
                        if (defined(color)) {
                            value = color.toHexStr();
                            this[memName] = value;
                            result = key + ":" + value + "|";
                        } else {
                            console.warn(`Invalid color value: ${value}. Using default value.`);
                        }
                    }
                }
            }
            return result;
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * plot.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state);
                }
            });
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid));
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * plot.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!");
                return false;
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New();
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid));
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid);
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar);
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i);
                let name = Element.prototype.NativeAPI.Property_GetName(pProp);
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp);
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true;
                    } else {
                        this.bVisible = false;
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar);
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j);
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc);
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc);
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType);
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc);
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid);
                if (pName == "PlottingComponent") {
                    this.componentPlottingUuid = uuid;
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z);
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp);
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp);
                        if (name == "PlottingType") {
                            this.type = value;
                        } else if (name == "EditMode") {
                            this.status = value;
                        } else if (name == "ControlPoints") {
                            this.controlPoints = json2Array(value);
                        } else if (name == "ControlPointColor") {
                            this.controlPointColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "ControlPointSize") {
                            this.controlPointSize = Number(value);
                        } else if (name == "ControlLineColor") {
                            this.controlLineColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "BottomHeight") {
                            this.baseHeight = Number(value);
                        } else if (name == "ControlLineWidth") {
                            this.controlLineWidth = Number(value);
                        } else if (name == "PlottingLineColor") {
                            this.lineColor = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "PlottingLineWidth") {
                            this.lineWidth = Number(value);
                        } else if (name == "PlottingSurfaceColor") {
                            this.surfaceColor = CooWasm.ColorObj.fromCssColorString(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid;
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z);
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp);
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp);
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value);
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value);
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar);
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid);
            return true;
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁图层后触发的函数回调。
         * @returns null 空值。
         * @example
         * plot = plot.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!");
                return null;
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state);
                }
                Element.prototype.Map.plottings = Element.prototype.Map.plottings.filter(item => item !== this);
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid);
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid);
                callbackSystem.remove("EditEnd", this.avatarUuid);
                callbackSystem.remove("", "LocateAvatar");
                callbackSystem.remove("RemoveAvatar", this.avatarUuid);
                this.avatarUuid = null;
                this.componentPlottingUuid = null;
                this.componentAltitudeVisCtrlUuid = null;
            });
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid));
            return null;
        }
    }
    return Plotting;
});